/** Turn a short editing comment into a new simulator message. */

function cleanup(text) {
  return text
    .replace(/[ \t]{2,}/g, " ")
    .replace(/[ \t]+([,.;])/g, "$1")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/ +\n/g, "\n")
    .trim();
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function normalizeComment(comment) {
  return comment
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b(mesg|mesage|messge|msg)\b/gi, "message")
    .replace(/\b(taht|thta)\b/gi, "that")
    .replace(/\b(shorterr|shortr)\b/gi, "shorter")
    .replace(/\braccourcir\b/gi, "raccourcis");
}

function splitSentences(text) {
  const flat = text.replace(/\s*\n+\s*/g, " ").trim();
  if (!flat) return [];
  return flat
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);
}

function shorten(text) {
  const lines = text.split("\n").map((line) => line.trimEnd());
  const bulletRe = /^\s*(?:✅|🎯|📖|⏱️|⭐|📸|📄|🎤|•|–|-)\s+/;
  const bulletLines = lines.filter((line) => bulletRe.test(line.trim()));
  if (bulletLines.length >= 3) {
    const intro = lines.find((line) => line.trim() && !bulletRe.test(line.trim())) || "";
    const closing = [...lines]
      .reverse()
      .find((line) => line.trim() && !bulletRe.test(line.trim()) && line !== intro);
    const closingSentences = closing ? splitSentences(closing) : [];
    const closingKeep =
      [...closingSentences].reverse().find((sentence) => sentence.includes("?")) ||
      closingSentences[0] ||
      "";
    const next = cleanup(
      [intro, ...bulletLines.slice(0, 2), closingKeep].filter(Boolean).join("\n")
    );
    if (next && next.length < text.trim().length) return next;
  }

  const sentences = splitSentences(text);
  if (sentences.length >= 3) {
    const question = [...sentences].reverse().find((sentence) => sentence.includes("?"));
    const head = sentences.slice(0, 2).join(" ");
    const next = cleanup(question && !head.includes(question) ? `${head} ${question}` : head);
    if (next.length < text.trim().length) return next;
  }
  if (sentences.length === 2) {
    const next = cleanup(sentences[0]);
    if (next.length < text.trim().length - 8) return next;
  }

  const dashCut = text.trim().split(/\s+[—–]\s+/)[0];
  if (dashCut && dashCut.length < text.trim().length - 12) {
    return cleanup(/[.!?…]$/.test(dashCut) ? dashCut : `${dashCut}.`);
  }
  return null;
}

function lengthen(text) {
  const extra = "On y va une étape à la fois.";
  if (text.includes(extra)) return null;
  return cleanup(`${text.trim()}\n${extra}`);
}

function stripEmoji(text) {
  const next = text.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{200D}]/gu, "");
  const cleaned = cleanup(next);
  if (!cleaned || cleaned === text.trim()) return null;
  return cleaned;
}

function removePhrase(text, phrase) {
  const trimmed = phrase.trim();
  if (!trimmed) return null;
  const re = new RegExp(escapeRegExp(trimmed), "gi");
  if (!re.test(text)) return null;
  const next = cleanup(text.replace(new RegExp(escapeRegExp(trimmed), "gi"), ""));
  if (!next || next === text.trim()) return null;
  return next;
}

function removeSelection(text, selection) {
  if (!selection || selection.end <= selection.start) return null;
  const selected = text.slice(selection.start, selection.end);
  if (!selected.trim()) return null;
  const next = cleanup(text.slice(0, selection.start) + text.slice(selection.end));
  if (!next) return null;
  return { text: next, selected: selected.trim() };
}

function quoted(value) {
  return value.replace(/^[«"“']|[»"”']$/g, "").trim();
}

function applyOne(text, rawComment, selection) {
  const comment = normalizeComment(rawComment);
  const lower = comment.toLowerCase();
  if (!comment) {
    return { ok: false, reason: "Écris un commentaire, par exemple « raccourcis » ou « retire ce mot »." };
  }

  const replaceMatch = comment.match(
    /^(?:remplace|replace|change)\s+[«"“']?(.+?)[»"”']?\s+(?:par|with|to|en)\s+[«"“']?(.+?)[»"”']?$/i
  );
  if (replaceMatch) {
    const from = quoted(replaceMatch[1]);
    const to = quoted(replaceMatch[2]);
    if (!from || !text.toLowerCase().includes(from.toLowerCase())) {
      return { ok: false, reason: `Je ne trouve pas « ${from} » dans ce message.` };
    }
    const next = cleanup(text.replace(new RegExp(escapeRegExp(from), "gi"), to));
    if (!next || next === text.trim()) {
      return { ok: false, reason: "Le remplacement ne change pas le message." };
    }
    return { ok: true, text: next, summary: `« ${from} » remplacé par « ${to} »` };
  }

  const addMatch = comment.match(/^(?:ajoute|ajouter|add|append)\s+[«"“']([^»"”']+)[»"”']/i);
  if (addMatch) {
    const extra = addMatch[1].trim();
    if (!extra || text.includes(extra)) {
      return { ok: false, reason: "Ce texte est déjà dans le message." };
    }
    return {
      ok: true,
      text: cleanup(`${text.trim()}\n${extra}`),
      summary: "Texte ajouté",
    };
  }

  if (
    /emoji|émoticône|emoticone/.test(lower) &&
    /retire|enl[eè]ve|supprime|sans|remove|without|no\b/.test(lower)
  ) {
    const next = stripEmoji(text);
    if (!next) return { ok: false, reason: "Ce message n'a pas d'emoji à retirer." };
    return { ok: true, text: next, summary: "Emojis retirés" };
  }

  const deictic =
    /^(?:remove|delete|drop|enl[eè]ve|enleve|supprime|retire|ôte|ote)\s+(?:that|this|ce|cet|cette)\s+(?:word|mot|phrase)$/i.test(
      comment
    );
  if (deictic) {
    const removed = removeSelection(text, selection);
    if (!removed) {
      return {
        ok: false,
        reason: "Sélectionne le mot dans le texte, ou écris retire « mot ».",
      };
    }
    return {
      ok: true,
      text: removed.text,
      summary: `Mot retiré : « ${removed.selected} »`,
    };
  }

  const named = comment.match(
    /^(?:remove|delete|drop|enl[eè]ve|enleve|supprime|retire|ôte|ote)\s+(?:the\s+|le\s+|la\s+|l['’]\s*|les\s+|ce\s+|cet\s+|cette\s+|that\s+|this\s+)?(?:word|mot|phrase)?\s*[:：]?\s*[«"“']?(.+?)[»"”']?$/i
  );
  if (named && !/shorter|plus court|raccourcis|longer|plus long/.test(lower)) {
    const phrase = quoted(named[1]);
    if (!phrase || /^(that|this|ce|word|mot|phrase)$/i.test(phrase)) {
      return {
        ok: false,
        reason: "Précise le mot entre guillemets, par exemple retire « virtuel ».",
      };
    }
    const next = removePhrase(text, phrase);
    if (!next) return { ok: false, reason: `Je ne trouve pas « ${phrase} » dans ce message.` };
    return { ok: true, text: next, summary: `Mot retiré : « ${phrase} »` };
  }

  if (/shorter|shorten|plus court|raccourcis|trop long|condense|abr[eè]ge/.test(lower)) {
    const next = shorten(text);
    if (!next) return { ok: false, reason: "Ce message est déjà court." };
    return { ok: true, text: next, summary: "Message raccourci" };
  }

  if (/longer|lengthen|plus long|d[eé]veloppe|rallonge|plus d[eé]taill/.test(lower)) {
    const next = lengthen(text);
    if (!next) return { ok: false, reason: "Ce message est déjà développé." };
    return { ok: true, text: next, summary: "Message allongé" };
  }

  return {
    ok: false,
    reason:
      "Commentaire non reconnu. Essaie « raccourcis », « retire « mot » », « retire ce mot » (avec une sélection) ou « remplace « a » par « b » ».",
  };
}

export function applyLabComment(text, comment, selection) {
  const source = text ?? "";
  const parts = normalizeComment(comment)
    .split(/\s+(?:and|et)\s+/i)
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) {
    return applyOne(source, "", selection);
  }

  let current = source;
  let activeSelection = selection;
  const summaries = [];
  for (const part of parts) {
    const result = applyOne(current, part, activeSelection);
    if (!result.ok) return result;
    current = result.text;
    summaries.push(result.summary);
    activeSelection = null;
  }

  if (!current || current === source.trim()) {
    return { ok: false, reason: "Le commentaire ne change pas le message." };
  }
  return { ok: true, text: current, summary: summaries.join(" · ") };
}
